import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  getDVIRTemplates,
  getDVIRTemplate,
  setSelectedTemplate,
  toggleShowAddTemplateModal,
  toggleShowEditTemplateModal,
  showDeleteTemplateConfirmationModal,
  resetAddQuestionToTemplate,
} from "../../dvirSlice";
import { DVIRTemplate } from "../../../../models";
import { TemplateQuestionsManager } from "../TemplateQuestionsManager/TemplateQuestionsManager";
import { AddTemplateQuestionModal } from "../../modals";
import "./DVIRTemplatesTab.scss";

const getTemplateTypeLabel = (type: string): string => {
  switch (type) {
    case "pre_trip":
      return "Pre-Trip";
    case "post_trip":
      return "Post-Trip";
    case "end_of_week":
      return "End of Week";
    default:
      return type;
  }
};

export function DVIRTemplatesTab() {
  const dispatch = useAppDispatch();
  const [showAddQuestionsModal, setShowAddQuestionsModal] = useState(false);

  const templates = useAppSelector((state) => state.dvir.templates);
  const selectedTemplate = useAppSelector(
    (state) => state.dvir.selectedTemplate
  );
  const getDVIRTemplatesAPI = useAppSelector(
    (state) => state.dvir.getDVIRTemplates
  );
  const getDVIRTemplateAPI = useAppSelector(
    (state) => state.dvir.getDVIRTemplate
  );

  React.useEffect(() => {
    dispatch(getDVIRTemplates());
  }, [dispatch]);

  React.useEffect(() => {
    if (templates.length > 0 && !selectedTemplate) {
      const firstTemplate = templates[0];
      dispatch(setSelectedTemplate(firstTemplate));
      dispatch(getDVIRTemplate(firstTemplate.id));
    }
  }, [templates, selectedTemplate, dispatch]);

  const handleSelectTemplate = (template: DVIRTemplate) => {
    dispatch(setSelectedTemplate(template));
    // Fetch full template details with questions
    dispatch(getDVIRTemplate(template.id));
  };

  const handleAddTemplate = () => {
    dispatch(toggleShowAddTemplateModal());
  };

  const handleEditTemplate = (template: DVIRTemplate) => {
    dispatch(setSelectedTemplate(template));
    dispatch(toggleShowEditTemplateModal());
  };

  const handleDeleteTemplate = (template: DVIRTemplate) => {
    dispatch(showDeleteTemplateConfirmationModal(template));
  };

  const handleAddQuestion = () => {
    setShowAddQuestionsModal(true);
  };

  const handleCloseAddQuestionsModal = () => {
    setShowAddQuestionsModal(false);
    dispatch(resetAddQuestionToTemplate());
  };

  if (getDVIRTemplatesAPI.loading) {
    return (
      <div className="dvir-templates-tab loading">
        <div className="loading-text">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="dvir-templates-tab">
      <div className="templates-layout">
        {/* Left Sidebar - Template List */}
        <div className="sidebar">
          <div className="sidebar-header">
            <h3>Templates</h3>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleAddTemplate}
            >
              + New
            </button>
          </div>

          {templates.length === 0 ? (
            <div className="empty-templates">
              <p>No templates found</p>
              <button className="btn btn-primary" onClick={handleAddTemplate}>
                Create First Template
              </button>
            </div>
          ) : (
            <div className="template-list">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className={`template-item ${
                    selectedTemplate?.id === template.id ? "active" : ""
                  }`}
                  onClick={() => handleSelectTemplate(template)}
                >
                  <div className="template-name">{template.name}</div>
                  <div className="template-meta">
                    <span className="template-type">
                      {getTemplateTypeLabel(template.templateType)}
                    </span>
                    <span className="divider">•</span>
                    <span
                      className={`status ${
                        template.isActive ? "active" : "inactive"
                      }`}
                    >
                      {template.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Panel - Template Details */}
        <div className="template-detail">
          {!selectedTemplate ? (
            <div className="no-selection">
              <h3>Select a template</h3>
              <p>
                Choose a template from the list to view and manage its
                questions.
              </p>
            </div>
          ) : getDVIRTemplateAPI.loading ? (
            <div className="loading-detail">
              <div className="loading-text">Loading template details...</div>
            </div>
          ) : (
            <div className="template-content">
              {/* Template Header */}
              <div className="template-header">
                <div className="header-info">
                  <h2>{selectedTemplate.name}</h2>
                  <p className="description">{selectedTemplate.description}</p>
                  <div className="template-badges">
                    <span
                      className={`status-badge ${
                        selectedTemplate.isActive ? "active" : "inactive"
                      }`}
                    >
                      {selectedTemplate.isActive ? "Active" : "Inactive"}
                    </span>
                    <span className="type-badge">
                      {getTemplateTypeLabel(selectedTemplate.templateType)}
                    </span>
                  </div>
                </div>
                <div className="header-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleEditTemplate(selectedTemplate)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteTemplate(selectedTemplate)}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Questions Section */}
              <div className="questions-section">
                <div className="section-header">
                  <h3>{`Questions (${selectedTemplate.templateQuestions.length})`}</h3>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleAddQuestion}
                  >
                    + Add Question
                  </button>
                </div>

                <div className="questions-container">
                  <TemplateQuestionsManager
                    templateId={selectedTemplate.id}
                    questions={selectedTemplate.templateQuestions || []}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Questions Modal */}
      {selectedTemplate && (
        <AddTemplateQuestionModal
          isOpen={showAddQuestionsModal}
          onClose={handleCloseAddQuestionsModal}
          templateId={selectedTemplate.id}
          existingQuestionIds={
            selectedTemplate.templateQuestions?.map((tq) => tq.questionId) || []
          }
        />
      )}
    </div>
  );
}
